import { useState, useEffect } from 'react';
import { useAuth } from '@/App';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Key, RefreshCw, Users, Calendar, Clock, CalendarPlus, Check } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AccessCodeManager = () => {
  const { token } = useAuth();
  const [currentCode, setCurrentCode] = useState(null);
  const [history, setHistory] = useState([]);
  const [allCodes, setAllCodes] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rotating, setRotating] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [activating, setActivating] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      const [currentRes, historyRes, allCodesRes] = await Promise.all([
        axios.get(`${API}/admin/current-access-code`, { headers }),
        axios.get(`${API}/admin/access-code-history`, { headers }),
        axios.get(`${API}/admin/all-access-codes`, { headers }).catch(() => ({ data: null }))
      ]);

      setCurrentCode(currentRes.data);
      setHistory(historyRes.data);
      if (allCodesRes.data) {
        setAllCodes(allCodesRes.data);
      }
    } catch (error) {
      console.error('Error fetching access code data:', error);
      toast.error('Error al cargar información de códigos');
    } finally {
      setLoading(false);
    }
  };

  const handleRotate = async () => {
    if (!window.confirm('¿Estás seguro de rotar el código de acceso? Todos los usuarios activos recibirán el nuevo código.')) {
      return;
    }

    setRotating(true);
    try {
      const response = await axios.post(
        `${API}/admin/rotate-access-code`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(`Código rotado exitosamente. ${response.data.users_updated} usuarios actualizados.`);
      fetchData();
    } catch (error) {
      console.error('Error rotating code:', error);
      toast.error('Error al rotar código');
    } finally {
      setRotating(false);
    }
  };

  const handleGenerateUntil2026 = async () => {
    if (!window.confirm('¿Generar códigos de acceso para todos los meses hasta diciembre 2026? Los códigos estarán inactivos y se activarán automáticamente el día 1 de cada mes.')) {
      return;
    }

    setGenerating(true);
    try {
      const response = await axios.post(
        `${API}/admin/generate-codes-until-2026`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(response.data.message);
      fetchData();
    } catch (error) {
      console.error('Error generating codes:', error);
      toast.error('Error al generar códigos');
    } finally {
      setGenerating(false);
    }
  };

  const handleActivateCurrentMonth = async () => {
    setActivating(true);
    try {
      const response = await axios.post(
        `${API}/admin/activate-current-month-code`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(response.data.message + ` (${response.data.users_updated || 0} usuarios actualizados)`);
      fetchData();
    } catch (error) {
      console.error('Error activating code:', error);
      toast.error('Error al activar código');
    } finally {
      setActivating(false);
    }
  };

  if (loading) {
    return (
      <Card className="card-viubux-dark border-purple-500/20">
        <CardContent className="py-12 text-center">
          <Clock className="w-12 h-12 mx-auto mb-4 text-purple-400 animate-spin" />
          <p className="verdana-regular text-white/70">Cargando...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Generate Codes Until 2026 */}
      <Card className="card-viubux-dark border-blue-500/30" data-testid="generate-codes-card">
        <CardHeader>
          <CardTitle className="gotham-bold text-white flex items-center gap-2">
            <CalendarPlus className="w-5 h-5" style={{ color: '#3b82f6' }} />
            Generar Códigos hasta 2026
          </CardTitle>
          <CardDescription className="verdana-regular text-white/70">
            Pre-genera códigos mensuales inactivos que se activarán automáticamente el día 1 de cada mes
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {allCodes && (
              <div className="bg-black/30 rounded-lg p-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                  <div>
                    <p className="text-white/60 text-sm">Total Códigos</p>
                    <p className="gotham-bold text-2xl text-white">{allCodes.total_count}</p>
                  </div>
                  {Object.entries(allCodes.by_year || {}).map(([year, codes]) => (
                    <div key={year}>
                      <p className="text-white/60 text-sm">{year}</p>
                      <p className="gotham-bold text-xl text-blue-400">{codes.length} meses</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            <div className="flex flex-col md:flex-row gap-3">
              <Button
                onClick={handleGenerateUntil2026}
                disabled={generating}
                className="flex-1 bg-blue-500/20 text-blue-400 hover:bg-blue-500/30 border border-blue-500/50"
                data-testid="generate-codes-btn"
              >
                <CalendarPlus className={`w-4 h-4 mr-2 ${generating ? 'animate-spin' : ''}`} />
                {generating ? 'Generando...' : 'Generar Códigos hasta 2026'}
              </Button>
              
              <Button
                onClick={handleActivateCurrentMonth}
                disabled={activating}
                className="flex-1 bg-green-500/20 text-green-400 hover:bg-green-500/30 border border-green-500/50"
                data-testid="activate-current-month-btn"
              >
                <Check className={`w-4 h-4 mr-2 ${activating ? 'animate-spin' : ''}`} />
                {activating ? 'Activando...' : 'Activar Código del Mes Actual'}
              </Button>
            </div>
            
            <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3">
              <p className="verdana-regular text-blue-200 text-sm">
                ℹ️ Los códigos se generan <strong>inactivos</strong> y se activan automáticamente a las <strong>00:01 hrs</strong> del día 1 de cada mes. También puedes activar manualmente el código del mes actual.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Current Code */}
      <Card className="card-viubux-dark border-green-500/30" data-testid="current-code-card">
        <CardHeader>
          <CardTitle className="gotham-bold text-white flex items-center gap-2">
            <Key className="w-5 h-5" style={{ color: '#10b981' }} />
            Código de Acceso Global Actual
          </CardTitle>
          <CardDescription className="verdana-regular text-white/70">
            Código compartido por todos los usuarios activos del mes
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="bg-black/30 rounded-lg p-6 text-center">
              <p className="verdana-regular text-white/70 text-sm mb-2">Código del Mes</p>
              <code className="gotham-black text-4xl block mb-4" style={{ color: '#10b981' }}>
                {currentCode?.code}
              </code>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="verdana-regular text-white/60">Válido desde:</p>
                  <p className="gotham-medium text-white">
                    {new Date(currentCode?.valid_from).toLocaleDateString('es-MX')}
                  </p>
                </div>
                <div>
                  <p className="verdana-regular text-white/60">Válido hasta:</p>
                  <p className="gotham-medium text-white">
                    {new Date(currentCode?.valid_until).toLocaleDateString('es-MX')}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-purple-500/10 rounded-lg p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Users className="w-8 h-8" style={{ color: '#7012b2' }} />
                <div>
                  <p className="verdana-regular text-white/70 text-sm">Usuarios Activos</p>
                  <p className="gotham-bold text-white text-2xl">{currentCode?.active_users}</p>
                </div>
              </div>
              
              <Button
                onClick={handleRotate}
                disabled={rotating}
                className="btn-viubux"
                data-testid="rotate-code-btn"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${rotating ? 'animate-spin' : ''}`} />
                {rotating ? 'Rotando...' : 'Rotar Código'}
              </Button>
            </div>

            <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3">
              <p className="verdana-regular text-yellow-200 text-sm">
                ℹ️ <strong>Rotación Manual:</strong> Usa este botón solo en emergencias. Los códigos pre-generados se activan automáticamente.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* History */}
      <Card className="card-viubux-dark border-purple-500/20" data-testid="code-history-card">
        <CardHeader>
          <CardTitle className="gotham-bold text-white flex items-center gap-2">
            <Calendar className="w-5 h-5" style={{ color: '#7012b2' }} />
            Historial de Códigos
          </CardTitle>
          <CardDescription className="verdana-regular text-white/70">
            Códigos de acceso generados (activos e inactivos)
          </CardDescription>
        </CardHeader>
        <CardContent>
          {history.length === 0 ? (
            <div className="text-center py-8 text-white/60 verdana-regular">
              No hay historial de códigos
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full verdana-regular">
                <thead>
                  <tr className="border-b border-purple-500/20">
                    <th className="text-left py-3 px-4 text-white/80">Código</th>
                    <th className="text-left py-3 px-4 text-white/80">Mes</th>
                    <th className="text-left py-3 px-4 text-white/80">Válido Desde</th>
                    <th className="text-left py-3 px-4 text-white/80">Válido Hasta</th>
                    <th className="text-left py-3 px-4 text-white/80">Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((code, idx) => (
                    <tr key={idx} className="border-b border-purple-500/10 hover:bg-purple-500/5" data-testid={`history-row-${idx}`}>
                      <td className="py-3 px-4 font-mono text-white gotham-bold">{code.code}</td>
                      <td className="py-3 px-4 text-white/70">{code.month}</td>
                      <td className="py-3 px-4 text-white/70">
                        {new Date(code.valid_from).toLocaleDateString('es-MX')}
                      </td>
                      <td className="py-3 px-4 text-white/70">
                        {new Date(code.valid_until).toLocaleDateString('es-MX')}
                      </td>
                      <td className="py-3 px-4">
                        <span className={`px-2 py-1 rounded-full text-xs ${
                          code.is_active
                            ? 'bg-green-500/20 text-green-400'
                            : 'bg-gray-500/20 text-gray-400'
                        }`}>
                          {code.is_active ? 'Activo' : 'Inactivo'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AccessCodeManager;
